import { useState } from 'react';
import { Button, Empty, Space, Spin, Typography } from 'antd';
import { useListDekitAreasQuery, useListTrackedCardsQuery } from '../../../redux/api/kit-staging';
import { StatusLegendPopup } from '../../../components/KitStaging/StatusLegendPopup';
import { DekitAreaView } from '../../../components/KitStaging/DekitAreaView';
import { AreaSubTabs } from '../../../components/KitStaging/AreaSubTabs';
import { BoardView } from './BoardBase';
import { groupByOli, OliGroup } from './groupByOli';

interface Props {
	view?: BoardView;
	selectedGroup?: OliGroup | null;
	onSelect?: (g: OliGroup | null) => void;
}

/** Dekit board — per-dekit-area display with show picked-up / show orphans
 *  toggles. Sub-tabs across the active dekit areas; tapping a card selects it
 *  into the shared left sidebar (which surfaces Mark Picked Up). The `view`
 *  prop is accepted for parity. */
export const DekitBoard = ({ selectedGroup = null, onSelect }: Props = {}) => {
	const { data: cards, isLoading } = useListTrackedCardsQuery({}, { pollingInterval: 10000 });
	const { data: dekitAreas } = useListDekitAreasQuery(undefined, { pollingInterval: 10000 });
	const [activeAreaId, setActiveAreaId] = useState<number | null>(null);
	const [legendOpen, setLegendOpen] = useState(false);

	const dkGroups = groupByOli(cards ?? []).filter(
		(g) => g.kitCard?.current_area === 'dekit' || g.materialCard?.current_area === 'dekit',
	);
	const subAreas = (dekitAreas ?? []).filter((a) => a.is_active);

	// DekitAreaView toggles on re-tap internally — just forward.
	const select = (g: OliGroup | null) => onSelect?.(g);

	return (
		<div style={{ padding: 16, width: '100%' }}>
			<Space style={{ justifyContent: 'space-between', width: '100%' }}>
				<Typography.Title level={3} style={{ margin: 0 }}>
					Dekit{' '}
					<Typography.Text type="secondary" style={{ fontSize: 14 }}>
						({dkGroups.length})
					</Typography.Text>
				</Typography.Title>
				<Button onClick={() => setLegendOpen(true)} aria-label="Show status legend">
					?
				</Button>
			</Space>

			<div style={{ marginTop: 12 }}>
				{isLoading ? (
					<div style={{ padding: 48, textAlign: 'center' }}>
						<Spin />
					</div>
				) : subAreas.length === 0 ? (
					<Empty style={{ marginTop: 48 }} description="No active dekit areas" />
				) : (
					<>
						<AreaSubTabs
							areas={subAreas}
							value={activeAreaId}
							onChange={setActiveAreaId}
							storageKey="kt_default_dekit_area_id"
						/>
						{activeAreaId != null && (
							<DekitAreaView
								areaId={activeAreaId}
								groups={dkGroups}
								onSelect={select}
								selectedGroup={selectedGroup}
							/>
						)}
					</>
				)}
			</div>

			<StatusLegendPopup open={legendOpen} onClose={() => setLegendOpen(false)} />
		</div>
	);
};
